import { Trans, useLingui } from "@lingui/react/macro";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";
import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupTextarea,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAutosizeTextarea } from "@/hooks/use-autosize-textarea";
import { swap } from "@/utils/array";
import { validateRe2RegexPattern } from "../../_validation/re2-regex-pattern";
import {
  LogicalOperator,
  URLGroupPatternType,
  URLGroupPatternTypeOptions,
} from "./constants";

export type URLGroupInputValue = {
  op: LogicalOperator;
  patterns: {
    id?: string;
    type: URLGroupPatternType;
    pattern: string;
    error?: string;
  }[];
};

type Props = {
  /**
   * 現在の値
   */
  value: URLGroupInputValue;
  /**
   * 値が変更されたときに呼び出されるコールバック
   */
  onChange: (value: URLGroupInputValue) => void;
  /**
   * ラベルを表示するかどうか
   * @default true
   */
  showLabels?: boolean;
};

type URLGroupPatternWithId = URLGroupInputValue["patterns"][0] & {
  id: string;
};

type URLGroupPatternRowProps = {
  pattern: URLGroupPatternWithId;
  index: number;
  total: number;
  op: LogicalOperator;
  operatorLabel: string;
  onChangeType: (index: number, type: URLGroupPatternType) => void;
  onChangePattern: (index: number, pattern: string) => void;
  onChangeOperator: (op: LogicalOperator) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRemove: (index: number) => void;
  i18n: ReturnType<typeof useLingui>["i18n"];
  t: ReturnType<typeof useLingui>["t"];
};

function URLGroupPatternRow({
  pattern,
  index,
  total,
  op,
  operatorLabel,
  onChangeType,
  onChangePattern,
  onChangeOperator,
  onMoveUp,
  onMoveDown,
  onRemove,
  i18n,
  t,
}: URLGroupPatternRowProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const resizeTextarea = useAutosizeTextarea(textareaRef);
  const isLast = index === total - 1;

  React.useLayoutEffect(() => {
    if (!isExpanded) return;
    resizeTextarea();
  }, [isExpanded, resizeTextarea]);

  return (
    <div className="flex items-start gap-2 flex-nowrap">
      <div className="flex-1 min-w-0 flex items-start gap-2">
        <Select
          value={pattern.type}
          onValueChange={(value) =>
            onChangeType(index, value as URLGroupPatternType)
          }
        >
          <SelectTrigger className="w-[152px] shrink-0 md:w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {URLGroupPatternTypeOptions.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {i18n._(label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1 min-w-0 flex flex-col">
          <InputGroup>
            {isExpanded ? (
              <InputGroupTextarea
                ref={textareaRef}
                value={pattern.pattern}
                onChange={(e) => {
                  resizeTextarea();
                  onChangePattern(index, e.target.value);
                }}
                rows={1}
                className="min-h-9 resize-none overflow-hidden"
                aria-invalid={Boolean(pattern.error)}
                required
              />
            ) : (
              <InputGroupInput
                value={pattern.pattern}
                onChange={(e) => onChangePattern(index, e.target.value)}
                aria-invalid={Boolean(pattern.error)}
                required
              />
            )}
            <InputGroupAddon
              align="inline-end"
              className={isExpanded ? "self-start pt-1" : undefined}
            >
              <InputGroupButton
                size="icon-sm"
                variant="ghost"
                type="button"
                onClick={() => setIsExpanded((prev) => !prev)}
                aria-label={
                  isExpanded ? t`Collapse pattern` : t`Expand pattern`
                }
              >
                {isExpanded ? (
                  <ChevronsDownUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronsUpDownIcon className="w-4 h-4" />
                )}
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
          {pattern.error && (
            <div className="text-xs text-destructive mt-1">{pattern.error}</div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 ml-auto">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              type="button"
              aria-label={t`Logical operator`}
              aria-hidden={isLast}
              disabled={isLast}
              className={`h-9 gap-1${isLast ? " invisible pointer-events-none" : ""}`}
            >
              {operatorLabel}
              <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-40">
            <DropdownMenuRadioGroup
              value={op}
              onValueChange={(value) =>
                onChangeOperator(value as LogicalOperator)
              }
            >
              <DropdownMenuRadioItem value="AND">
                <div className="flex flex-col">
                  <span>
                    <Trans>AND</Trans>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <Trans>Matches all patterns</Trans>
                  </span>
                </div>
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="OR">
                <div className="flex flex-col">
                  <span>
                    <Trans>OR</Trans>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <Trans>Matches any pattern</Trans>
                  </span>
                </div>
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onMoveUp(index)}
          type="button"
          disabled={index === 0}
          className="h-8 w-8"
        >
          <ArrowUpIcon className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onMoveDown(index)}
          type="button"
          disabled={isLast}
          className="h-8 w-8"
        >
          <ArrowDownIcon className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onRemove(index)}
          className="text-destructive h-8 w-8"
          type="button"
          disabled={total === 1}
        >
          <TrashIcon className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * URLグループ入力コンポーネント
 *
 * このコンポーネントは、URLグループの入力部分のみを提供します。
 * Index Worker フォームダイアログなどに組み込んで使用します。
 */
export default function URLGroupInput({
  value,
  onChange,
  showLabels = true,
}: Props) {
  const { i18n, t } = useLingui();
  const { op } = value;
  const patterns = value.patterns;
  const operatorLabel = op === "AND" ? t`AND` : t`OR`;
  const [patternIds, setPatternIds] = React.useState<string[]>(() =>
    patterns.map((pattern) => pattern.id ?? crypto.randomUUID()),
  );

  React.useEffect(() => {
    setPatternIds((prev) => {
      if (patterns.length === prev.length) {
        const hasExternalIds = patterns.some((pattern) => pattern.id);
        if (!hasExternalIds) return prev;
      }
      return patterns.map(
        (pattern, index) => pattern.id ?? prev[index] ?? crypto.randomUUID(),
      );
    });
  }, [patterns]);

  const patternsWithIds: URLGroupPatternWithId[] = patterns.map(
    (pattern, index) => ({
      ...pattern,
      id: patternIds[index] ?? crypto.randomUUID(),
    }),
  );

  const handleChangeType = (index: number, type: URLGroupPatternType) => {
    const newPatterns = patternsWithIds.map((p, i) => {
      if (i !== index) return p;
      const newPattern: typeof p = {
        id: p.id,
        type: type,
        pattern: p.pattern,
        error: validateRe2RegexPattern(type, p.pattern) ?? undefined,
      };
      return newPattern;
    });

    onChange({
      ...value,
      patterns: newPatterns.map(({ id: _, ...rest }) => rest),
    });
  };

  const handleChangePattern = (index: number, pattern: string) => {
    const newPatterns = patternsWithIds.map((p, i) => {
      if (i !== index) return p;
      const newPattern: typeof p = {
        id: p.id,
        type: p.type,
        pattern: pattern,
        error: validateRe2RegexPattern(p.type, pattern) ?? undefined,
      };
      return newPattern;
    });

    onChange({
      ...value,
      patterns: newPatterns.map(({ id: _, ...rest }) => rest),
    });
  };

  const handleChangeOperator = (op: LogicalOperator) => {
    onChange({
      ...value,
      op,
    });
  };

  const moveToUp = (index: number) => {
    if (index === 0) return;
    setPatternIds((prev) => swap(prev, index, index - 1));
    onChange({
      ...value,
      patterns: swap(patterns, index, index - 1),
    });
  };

  const moveToDown = (index: number) => {
    if (index === patternsWithIds.length - 1) return;
    setPatternIds((prev) => swap(prev, index, index + 1));
    onChange({
      ...value,
      patterns: swap(patterns, index, index + 1),
    });
  };

  const removeOption = (index: number) => {
    setPatternIds((prev) => prev.filter((_, i) => i !== index));
    onChange({
      ...value,
      patterns: patterns.filter((_, i) => i !== index),
    });
  };

  const addOption = () => {
    setPatternIds((prev) => [...prev, crypto.randomUUID()]);
    onChange({
      ...value,
      patterns: [...patterns, { type: "CONTAINS", pattern: "" }],
    });
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {showLabels && (
        <div className="text-sm">
          <Trans>Patterns</Trans>
        </div>
      )}
      <div className="flex min-w-0 flex-col gap-2">
        <div className="w-full overflow-x-auto">
          <div className="min-w-[640px] space-y-2">
            {patternsWithIds.map((pattern, index) => (
              <URLGroupPatternRow
                key={pattern.id}
                pattern={pattern}
                index={index}
                total={patternsWithIds.length}
                op={op}
                operatorLabel={operatorLabel}
                onChangeType={handleChangeType}
                onChangePattern={handleChangePattern}
                onChangeOperator={handleChangeOperator}
                onMoveUp={moveToUp}
                onMoveDown={moveToDown}
                onRemove={removeOption}
                i18n={i18n}
                t={t}
              />
            ))}
            <Button
              variant="outline"
              onClick={addOption}
              type="button"
              className="w-full mt-2"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              <Trans>Add pattern</Trans>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
